import React from "react";
import { IonPage, useIonViewWillEnter } from "@ionic/react";
import { useLocation } from "react-router-dom";

import { useWaitApiCall } from "../../../hooks/useWaitApiCall";
import useToast from "../../../hooks/useToast";
import Toast from "../../../molecules/Toast";
import RealizationForm from "../../../templates/RealizationForm";
import {
  categorizedRealizationPath,
  clearSuccessMessage,
  Context,
  createCategory,
  createRealization,
  CREATE_CATEGORY_SUCCESS_MESSAGE,
  CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE,
  CREATE_REALIZATION_SUCCESS_MESSAGE,
  fetchCategories,
  fetchClasses,
  fetchGearBoxes,
  fetchTeachers,
  updateReadAtToRealizationFeedback,
  newRealizationPath,
  postRealizationFeedbackEvaluation,
  createRealizationFeedbacks,
  fetchRealizationFeedbacks,
  CREATE_REALIZATION_FEEDBACKS_SUCCESS_MESSAGE,
  CREATE_REALIZATION_FAILURE_MESSAGE,
  createRealizationConversation,
  submitPulseSurveyAnswers,
  submitDeclinePulseSurveyAnswers,
  SUBMIT_PULSE_SURVEY_ANSWERS_SUCCESS_MESSAGE,
  SUBMIT_PULSE_SURVEY_ANSWERS_FAILURE_MESSAGE,
} from "../../../store/student";
import {
  ICategory,
  IRealizationCreateParams,
  IRealizationImage,
  EvaluationType,
  IPulseSurveyAnswer,
} from "../../../state";
import styles from "../common.module.scss";
import { calcUnreadNotiCount } from "../../../libs/calcUnreadNotiCount";

const THEME_KEY = "hasTheme";

const NewRealization = (props: { history: any }) => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const { pathname, search } = useLocation();
  const { toast, setToast, handleToast } = useToast();

  const hasTheme = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get(THEME_KEY) === "true";
  }, [search]);

  const createRealizationFunc = React.useCallback(
    (
      realization: IRealizationCreateParams,
      submit_teacher_ids: number[],
      share_class_ids: number[],
      gearbox_id: number | null,
      image?: IRealizationImage,
      geartheme_id?: number,
    ) => {
      if (calling) return;
      dispatch(
        peep(
          createRealization(
            realization,
            submit_teacher_ids,
            share_class_ids,
            gearbox_id,
            image,
            geartheme_id,
          ),
        ),
      );
    },
    [calling, dispatch, peep],
  );

  const createCategoryFunc = React.useCallback(
    (category: ICategory) => {
      if (calling) return;
      dispatch(peep(createCategory(category)));
    },
    [calling, dispatch, peep],
  );

  const updateReadAtToRealizationFeedbackFunc = React.useCallback(
    (realization_feedback_id: number) => {
      dispatch(
        peep(updateReadAtToRealizationFeedback(realization_feedback_id)),
      );
    },
    [dispatch, peep],
  );

  const submitRealizationFeedbackEvaluation = (
    id: number,
    type: EvaluationType,
  ) => {
    dispatch(peep(postRealizationFeedbackEvaluation(id, type)));
  };

  const submitPulseSurveyAnswersFunc = (answers: IPulseSurveyAnswer) => {
    dispatch(peep(submitPulseSurveyAnswers(answers)));
  };

  const declinePulseSurveyAnswersFunc = () => {
    dispatch(peep(submitDeclinePulseSurveyAnswers()));
  };

  useIonViewWillEnter(() => {
    dispatch(fetchCategories());
    dispatch(fetchTeachers());
    dispatch(fetchClasses());
    dispatch(fetchGearBoxes({ is_check_archived: true }));
  });

  React.useEffect(() => {
    if (calling) return;
    const categoryIDs = contextState.realization?.categories?.map(
      category => category.id,
    );

    if (
      pathname.includes(newRealizationPath) &&
      contextState.success_message === CREATE_CATEGORY_SUCCESS_MESSAGE
    ) {
      dispatch(clearSuccessMessage());
      dispatch(peep(fetchCategories()));
    } else if (
      contextState.success_message === CREATE_REALIZATION_SUCCESS_MESSAGE ||
      contextState.success_message ===
        CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE
    ) {
      dispatch(clearSuccessMessage());
      props.history.push(
        `${categorizedRealizationPath}/${categoryIDs?.[0] ?? 0}`,
      );
    }
  }, [
    dispatch,
    contextState.success_message,
    contextState.realization,
    props.history,
    calling,
    peep,
    pathname,
  ]);

  const createRealizationFeedbacksFunc = React.useCallback(() => {
    dispatch(peep(createRealizationFeedbacks()));
  }, [dispatch, peep]);

  React.useEffect(() => {
    if (
      contextState.success_message ===
      CREATE_REALIZATION_FEEDBACKS_SUCCESS_MESSAGE
    ) {
      dispatch(peep(fetchRealizationFeedbacks()));
    }
  }, [dispatch, peep, contextState.success_message]);

  React.useEffect(() => {
    const successMessages = [SUBMIT_PULSE_SURVEY_ANSWERS_SUCCESS_MESSAGE];
    const errorMessages = [
      CREATE_REALIZATION_FAILURE_MESSAGE,
      SUBMIT_PULSE_SURVEY_ANSWERS_FAILURE_MESSAGE,
    ];

    handleToast("success", contextState.success_message, successMessages);
    handleToast("danger", contextState.error, errorMessages);
  }, [contextState.success_message, contextState.error, handleToast]);

  const createRealizationConversationFunc = React.useCallback(
    (realization_id: number) => {
      dispatch(peep(createRealizationConversation(realization_id)));
    },
    [dispatch, peep],
  );

  return (
    <IonPage className={styles.realizationWrapper}>
      <Toast
        type={toast.type}
        showToast={toast.isShow}
        onClose={() => setToast({ ...toast, isShow: false })}
        message={toast.message}
      />
      <RealizationForm
        calling={calling}
        createRealization={createRealizationFunc}
        createCategory={createCategoryFunc}
        pathname={pathname}
        success_message={contextState.success_message}
        categories={contextState.categories}
        teachers={contextState.teachers}
        classes={contextState.classes}
        gearBoxes={contextState.gearBoxes}
        stock_c_to_c_enabled={contextState.stock_c_to_c_enabled}
        realization_feedback_creatable={
          contextState.realization_feedback_creatable
        }
        gearTheme={hasTheme ? contextState.geartheme : null}
        unreadNotiCount={calcUnreadNotiCount(contextState)}
        student_realization_feedbacks={
          contextState.student_realization_feedbacks
        }
        submitRealizationFeedbackEvaluation={
          submitRealizationFeedbackEvaluation
        }
        createRealizationFeedbacks={createRealizationFeedbacksFunc}
        updateReadAtToRealizationFeedback={
          updateReadAtToRealizationFeedbackFunc
        }
        createRealizationConversation={createRealizationConversationFunc}
        submitPulseSurveyAnswers={submitPulseSurveyAnswersFunc}
        declinePulseSurveyAnswers={declinePulseSurveyAnswersFunc}
      />
    </IonPage>
  );
};

export default NewRealization;
